import cx from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { useMedia } from 'react-use-media';
import useLanguage from '~/i18n';

import NextGenCommentList from '~/Share/containers/NextGenCommentList';
import AuthorMessage from '~/Share/components/AuthorMessage';
import SidebarProfile from '~/Share/components/SidebarProfile';
import ShareButton from '~/Share/components/NextGen/ShareButton';
import Video from '~/Share/components/NextGen/Video';
import MessageTemplateEmbeds from '~/Share/containers/MessageTemplateEmbeds';
import Report from '~/Share/components/Report';
import { LandingPageType, ReduxState } from '~/types/redux/share';
import { isAndroid } from '~/utils/agent';
import { LogoImage } from './LogoImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReplyPanelContainer from '~/Share/containers/ReplyPanelContainer';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { addUtmToLink } from '~/utils/add-utm-to-link';

const AuthorMessageContainer = styled.div`
  padding: 32px;
  border-bottom: 1px solid #eee;
  background-color: white;
  flex: auto;
`;

const StyledReport = styled(Report)`
  display: block;
  align-self: center;
  margin-top: 5px;
  margin-bottom: 50px !important;

  @media (min-width: 600px) {
    display: none !important;
  }
`;

const SidebarOptions = styled.div`
  border-top: 1px solid #eee;
  background: white !important;

  @media (max-width: 600px) {
    height: auto;
  }

  @media only screen and (min-width: 600px) and (max-width: 850px) {
    width: 622px;
    align-self: center;
    display: flex;
    justify-content: center;
  }
`;

const Scrollable = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ActionContainer = styled.div`
  @media (max-width: 600px) {
    margin-right: 18px;
    i {
      font-size: 23px;
    }
  }
`;

const SidebarContent = styled.div<{ expand: boolean }>`
  @media (max-width: 600px) {
    height: ${(props) => (props.expand ? '100vh' : 'auto')} !important;
  }
`;

const Container = styled.div<{ height: string }>`
  height: ${(props) => props.height} !important;
`;

export type SidebarProps = {
  singleColumnLayout: boolean;
  hideReport?: boolean;
  showSocialShare?: boolean;
};

export default function Sidebar({
  singleColumnLayout,
  hideReport,
  showSocialShare,
}: SidebarProps) {
  const [isReplying, setIsReplying] = useState(false);
  const [screenHeight, setScreenHeight] = useState('100%');

  const isMobile = useMedia({
    maxWidth: 850,
  });

  const {
    model,
    assignee,
    comments = [],
    isRecipient = false,
    messageTemplate,
    landingPageType,
    features,
    hasCustomLogo,
  } = useSelector((state: ReduxState) => ({
    model: state.root.model,
    assignee: state.root.assignee,
    comments: state.comments?.comments,
    isRecipient: state.root.isRecipient,
    messageTemplate: state.root.messageTemplate,
    landingPageType: state.root.type,
    features: state.root.features,
    hasCustomLogo: state.root.messageTemplate.has_custom_logo,
  }));

  const updateScreenHeight = useCallback(() => {
    setScreenHeight(`${window.innerHeight}px`);
  }, []);

  //handle android chrome address bar hide on scroll
  useEffect(() => {
    if (!isAndroid()) {
      return;
    }
    window.addEventListener('resize', updateScreenHeight);
    return () => window.removeEventListener('resize', updateScreenHeight);
  }, [isAndroid]);

  void useLanguage(assignee.locale);

  return (
    <Container className='nextgen-sidebar' height={screenHeight}>
      <Scrollable>
        <SidebarContent className='sidebar-content' expand={isReplying}>
          {singleColumnLayout && !isReplying && (
            <div className='flex flex-col bg-white sm:bg-transparent'>
              <LogoImage
                imageUrl={
                  features.canCustomiseLandingPage
                    ? messageTemplate.logo_url
                    : undefined
                }
                anchorUrl={
                  !hasCustomLogo
                    ? addUtmToLink('https://www.bonjoro.com', {
                        utm_source: 'video_messaging_landing_page',
                        utm_medium: isMobile
                          ? 'bonjoro_logo-mobile'
                          : 'bonjoro_logo-desktop',
                        utm_campaign: 'video_messaging_plg',
                      })
                    : undefined
                }
              />
              <Video
                useDefaultPlayer={landingPageType !== LandingPageType.Greet}
                videoUrl={model.video_url}
                showSingleColumnLayout={singleColumnLayout}
              />
            </div>
          )}

          <div className='profile-container'>
            <SidebarProfile {...assignee} />
            <ActionContainer>
              <FontAwesomeIcon
                className={cx('text-[28px] visible xmd:invisible', {
                  hidden: !isReplying,
                })}
                icon={faTimes}
                onClick={() => setIsReplying(false)}
              />

              {showSocialShare && (
                <ShareButton
                  className={cx({
                    hidden: !isMobile || (isReplying && isMobile),
                  })}
                />
              )}
            </ActionContainer>
          </div>

          <AuthorMessageContainer className='author-message'>
            <AuthorMessage
              model={model}
              assignee={assignee}
              messageTemplate={messageTemplate}
              landingPageType={landingPageType}
            />
          </AuthorMessageContainer>

          {isMobile && (comments.length > 0 || isReplying) && (
            <NextGenCommentList
              comments={comments}
              assignee={assignee}
              showCommentList={isRecipient}
              expand={isReplying}
            />
          )}

          {singleColumnLayout && (
            <div
              className={cx('media-embeds flex flex-col pt-[18px]', {
                hidden: isReplying,
              })}
            >
              <MessageTemplateEmbeds messageTemplate={messageTemplate} />
              {!hideReport && (
                <StyledReport
                  modelId={model.id}
                  landingPageType={landingPageType}
                  showLabel
                />
              )}
            </div>
          )}

          {!isMobile && (
            <NextGenCommentList
              comments={comments}
              assignee={assignee}
              showCommentList={isRecipient}
            />
          )}
        </SidebarContent>
      </Scrollable>
      {isRecipient && (
        <SidebarOptions className='sidebar-options'>
          <ReplyPanelContainer
            onFocus={() => setIsReplying(true)}
            allowCompactBehavior={isMobile}
          />
        </SidebarOptions>
      )}
    </Container>
  );
}
